/**
 * Correct the serial recorded against a checked-in device.
 *
 * Deliberately narrow: it PATCHes the inspection blob with one field swapped
 * and never sends `inspectionStatus`, so correcting a typo can't re-open a
 * completed inspection the way stepping back through the wizard does (each
 * wizard Next writes `in_progress`). Reopen the wizard when more than the
 * serial is wrong.
 *
 * Raised from wherever the wrong serial is visible — the device's Done
 * summary and the Device row on a job — so the same shell is shared.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import type { CheckInDraftJob } from '@techgitter/contracts';

import { INSPECTION_SCHEMA } from '../../../utils/inspectionSchema';
import {
  applySerialCorrection,
  isSerialUnchanged,
  validateSerialCorrection,
} from '../../../utils/serialCorrection';
import { patchCheckInDraftJob } from '../../../services/checkInApi';
import '../sessionDetail/dialogs/checkInDialog.css';

export interface CorrectSerialDialogProps {
  open: boolean;
  sessionId: string;
  /** Needs the full draft job — the serial lives inside its inspection blob. */
  draftJob: CheckInDraftJob;
  onClose: () => void;
  /** Refetch the session so every live reader picks the new value up. */
  onSaved: () => void;
}

export const CorrectSerialDialog: React.FC<CorrectSerialDialogProps> = ({
  open,
  sessionId,
  draftJob,
  onClose,
  onSaved,
}) => {
  const inspection = draftJob.inspection ?? null;
  const schema = INSPECTION_SCHEMA[draftJob.deviceType];
  // Phones record the IMEI in the same field.
  const label = draftJob.deviceType === 'phone' ? 'IMEI' : 'Serial number';

  const [serial, setSerial] = useState('');
  const [notLegible, setNotLegible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed from the stored inspection each time it opens, so a cancelled
  // edit doesn't linger into the next one.
  useEffect(() => {
    if (!open) return;
    setSerial(inspection?.serialNumber ?? '');
    setNotLegible(
      inspection && 'serialNotLegible' in inspection
        ? Boolean(inspection.serialNotLegible)
        : false,
    );
    setError(null);
    const id = window.setTimeout(() => inputRef.current?.select(), 0);
    return () => window.clearTimeout(id);
  }, [open, inspection]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, submitting, onClose]);

  if (!open || !inspection) return null;

  const previous =
    inspection.serialNumber ||
    ('serialNotLegible' in inspection && inspection.serialNotLegible ? 'Not legible' : '—');

  const handleSubmit = async () => {
    const correction = { serialNumber: serial, serialNotLegible: notLegible };
    const message = validateSerialCorrection(schema, inspection.manufacturer, correction);
    if (message) {
      setError(message);
      return;
    }
    if (isSerialUnchanged(inspection, schema, correction)) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await patchCheckInDraftJob(sessionId, draftJob.id, {
        inspection: applySerialCorrection(inspection, schema, correction),
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the correction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label={`Correct ${label}`}>
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Correct {label.toLowerCase()}</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__desc">
            Corrects the recorded {label.toLowerCase()} only. The rest of the inspection and
            its completed status are left as they are.
          </div>

          <div className="ci-dialog__was">
            Currently recorded: <strong>{previous}</strong>
          </div>

          <label className="ci-dialog__field-label" htmlFor="correct-serial-value">
            {label}
          </label>
          <input
            id="correct-serial-value"
            ref={inputRef}
            className={`ci-dialog__input${error ? ' ci-dialog__input--error' : ''}`}
            value={serial}
            onChange={e => {
              setSerial(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit();
            }}
            disabled={submitting}
            autoComplete="off"
            spellCheck={false}
          />

          {schema.supportsSerialNotLegible && (
            <label
              className={`ci-dialog__check${serial.trim() ? ' ci-dialog__check--disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={notLegible && !serial.trim()}
                // A serial that is present contradicts "not legible", so the
                // tick is only meaningful while the field is empty.
                disabled={submitting || !!serial.trim()}
                onChange={e => {
                  setNotLegible(e.target.checked);
                  if (error) setError(null);
                }}
              />
              {label} is not legible on the device
            </label>
          )}

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button
            type="button"
            className="ci-dialog__cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Saving…' : 'Save correction'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
